import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import { tokenUtils } from '../index';
const AuthContext = createContext(undefined);
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        initializeAuth();
    }, []);
    useEffect(() => {
        // Set up automatic token refresh
        let refreshInterval;
        if (user) {
            refreshInterval = setInterval(async () => {
                try {
                    const accessToken = await tokenUtils.getAccessToken();
                    if (accessToken) {
                        const expiration = tokenUtils.getTokenExpiration(accessToken);
                        if (expiration) {
                            const timeUntilExpiry = expiration - Date.now();
                            // Refresh token 5 minutes before expiry
                            if (timeUntilExpiry < 5 * 60 * 1000) {
                                // Note: Token refresh should be handled by the API client interceptors
                                // This is just a fallback check
                            }
                        }
                    }
                }
                catch (error) {
                    console.error('Error refreshing token:', error);
                    // If refresh fails, log out user
                    await logout();
                }
            }, 60 * 1000); // Check every minute
        }
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [user]);
    const initializeAuth = async () => {
        try {
            const userData = await tokenUtils.getUserData();
            const accessToken = await tokenUtils.getAccessToken();
            if (userData && accessToken && !tokenUtils.isTokenExpired(accessToken)) {
                setUser(userData);
            }
            else {
                // Token is expired or missing, clear everything
                await tokenUtils.clearAllTokens();
            }
        }
        catch (error) {
            console.error('Error initializing auth:', error);
            await tokenUtils.clearAllTokens();
        }
        finally {
            setIsLoading(false);
        }
    };
    const login = async (user, tokens) => {
        await tokenUtils.setAccessToken(tokens.access);
        await tokenUtils.setRefreshToken(tokens.refresh);
        await tokenUtils.setUserData(user);
        setUser(user);
    };
    const logout = async () => {
        await tokenUtils.clearAllTokens();
        setUser(null);
    };
    const updateUser = async (updatedUser) => {
        await tokenUtils.setUserData(updatedUser);
        setUser(updatedUser);
    };
    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        updateUser,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuthContext() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
}
